<p-toast [life]="10000"></p-toast>
@if (isLoading) {
<p-progressBar mode="indeterminate" class="m-2"></p-progressBar>
}
<app-page-shell>
    <app-page-hero
        eyebrow="Sales Order Intake"
        [title]="editMode ? 'Edit Sales Order' : 'Create Sales Order'"
        subtitle="Capture customer ownership, commercial terms, priced lines, and readiness before confirmation.">
        <div actions>
            <input #aiOrderDocumentInput type="file" class="hidden" accept=".pdf,image/png,image/jpeg,image/webp,text/plain"
                   (change)="onAiOrderDocumentSelected($event)">
            <p-button
                label="AI Upload"
                icon="pi pi-sparkles"
                severity="secondary"
                [outlined]="true"
                [loading]="isAiParsing"
                [disabled]="isLoading"
                pTooltip="Upload a sales order document and fill the form with AI"
                (click)="aiOrderDocumentInput.click()">
            </p-button>
            <p-button
                label="Cancel"
                icon="pi pi-times"
                severity="secondary"
                [outlined]="true"
                (click)="cancel()">
            </p-button>
        </div>
    </app-page-hero>

    <app-page-metric-strip [metrics]="formMetrics()"></app-page-metric-strip>

    <div class="p-3 mb-3 border-round border-1 border-orange-300 bg-orange-50 text-orange-900 flex align-items-center gap-2" *ngIf="isLocked">
        <i class="pi pi-lock text-xl text-orange-600"></i>
        <div>
            <strong>Order Locked</strong>
            <div>{{ lockReason }}</div>
        </div>
    </div>

    @if (executionLineage?.sources?.length) {
    <section class="execution-lineage-panel" aria-label="Execution source history">
        <div class="execution-lineage-heading">
            <div>
                <span class="execution-lineage-eyebrow">Execution-driven draft</span>
                <h3>Source snapshots</h3>
            </div>
            <div class="execution-source-badges">
                @for (source of executionLineage!.sources; track source.sourceType + ':' + source.sourceId) {
                <span>{{ source.sourceType.replaceAll('_', ' ') }} · {{ source.sourceNumber || source.sourceId }}</span>
                }
            </div>
        </div>
        <div class="execution-lineage-table-wrap">
            <table class="execution-lineage-table">
                <thead><tr><th>Origin</th><th>Original</th><th>Current</th><th>Status</th></tr></thead>
                <tbody>
                    @for (line of executionLineage!.lines; track line.snapshotId) {
                    <tr [class.execution-line-changed]="line.changed">
                        <td><strong>{{ line.operationalCode || line.sourceResourceType }}</strong><small>{{ line.sourceResourceNumber || line.sourceResourceId }}</small></td>
                        <td>{{ line.originalQuantity ?? '—' }} × {{ line.originalUnitPrice ?? '—' }} {{ line.originalCurrencyCode }}</td>
                        <td>{{ line.currentQuantity ?? 'Removed' }} @if (line.currentUnitPrice != null) { × {{ line.currentUnitPrice }} {{ line.originalCurrencyCode }} }</td>
                        <td><span class="execution-line-state">{{ line.lineState }}</span>@if (line.changed) { <small>Edited from source</small> }</td>
                    </tr>
                    }
                </tbody>
            </table>
        </div>
    </section>
    }

    <app-customer-form
        [displayModal]="showCustomerForm"
        [dialogHeader]="customerFormHeader"
        [submitButtonLabel]="customerFormSubmitLabel"
        [company]="selectedCompany"
        [customer]="selectedCustomer"
        (modalClosed)="onCustomerFormClosed($event)">
    </app-customer-form>

    <app-company-form
        [displayModal]="showCompanyForm"
        [dialogHeader]="companyFormHeader"
        [submitButtonLabel]="companyFormSubmitLabel"
        [company]="selectedCompany"
        (modalClosed)="onCompanyFormClosed($event)">
    </app-company-form>

    <form id="sales-order-form" [formGroup]="salesOrderForm" (ngSubmit)="saveSalesOrder()">
        <div class="order-intake-layout p-fluid">
            <div class="order-intake-main">
                <app-page-form-section
                    icon="pi pi-briefcase"
                    title="Party & Source"
                    [subtitle]="isCrmPartySource() ? 'Use CRM context to derive the party and retain the customer relationship.' : 'Select the required main party and, when applicable, an optional sub party.'">
                    <div class="formgrid grid">
                        @if (editMode) {
                        <div class="field col-12 md:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="orderNumber" type="text" pInputText formControlName="orderNumber" readonly />
                                    <label for="orderNumber">Order Number</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        }

                        <div class="field col-12" [ngClass]="editMode ? 'md:col-8' : 'md:col-6'">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-bookmark"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="externalReferenceNumber" type="text" pInputText formControlName="externalReferenceNumber" placeholder="e.g. Client PO / Reference #" />
                                    <label for="externalReferenceNumber">External Client Reference #</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>

                        <div class="field col-12">
                            <app-commercial-party-picker
                                [owningCompanyId]="commercialPartyOwningCompanyId"
                                [value]="commercialPartyReference"
                                [required]="true"
                                label="Customer Commercial Party"
                                (quickCreated)="onCrmQuickCreated($event)"
                                (valueChange)="onCommercialPartyChange($event)">
                            </app-commercial-party-picker>
                        </div>
                    </div>
                </app-page-form-section>

                <app-page-form-section
                    icon="pi pi-wallet"
                    title="Commercial Terms"
                    subtitle="Set ownership, payment terms, dates, currency, and project context.">
                    <div class="formgrid grid">
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select
                                        id="salesperson"
                                        [options]="salespersons"
                                        formControlName="salesperson"
                                        optionLabel="label"
                                        optionValue="value"
                                        dataKey="id"
                                        styleClass="w-full">
                                    </p-select>
                                    <label for="salesperson">Salesperson</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-2">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-credit-card"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select
                                        id="paymentTerm"
                                        [options]="paymentTerms"
                                        formControlName="paymentTerm"
                                        optionLabel="label"
                                        optionValue="value"
                                        dataKey="paymentTermID"
                                        styleClass="w-full">
                                    </p-select>
                                    <label for="paymentTerm">Payment Term</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            @if (salesOrderForm.get('paymentTerm')?.invalid && salesOrderForm.get('paymentTerm')?.touched) {
                            <small class="p-error">Payment term is required.</small>
                            }
                        </div>
                        <div class="field col-12 md:col-6 xl:col-6">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <app-logistics-project-lookup inputId="logisticsProjectId" formControlName="logisticsProjectId"></app-logistics-project-lookup>
                                    <label for="logisticsProjectId">Logistics Project</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-datePicker
                                        inputId="orderDate"
                                        formControlName="orderDate"
                                        dateFormat="yy-mm-dd"
                                        [showIcon]="false"
                                        styleClass="w-full">
                                    </p-datePicker>
                                    <label for="orderDate">Order Date</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            @if (salesOrderForm.get('orderDate')?.invalid && salesOrderForm.get('orderDate')?.touched) {
                            <small class="p-error">Order date is required.</small>
                            }
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-calendar-plus"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-datePicker
                                        inputId="expectedDate"
                                        formControlName="expectedDate"
                                        dateFormat="yy-mm-dd"
                                        [showIcon]="false"
                                        styleClass="w-full">
                                    </p-datePicker>
                                    <label for="expectedDate">Expected Date</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            @if (salesOrderForm.get('expectedDate')?.invalid && salesOrderForm.get('expectedDate')?.touched) {
                            <small class="p-error">Expected date is required.</small>
                            }
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select inputId="shipToLocationId" formControlName="shipToLocationId"
                                        appLazyLocationPicker optionLabel="name" optionValue="id"
                                        [filter]="true" [showClear]="true" styleClass="w-full">
                                    </p-select>
                                    <label for="shipToLocationId">Ship-to Location</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            <small class="text-secondary">Required before warehouse fulfillment.</small>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-clock"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-datePicker inputId="deliveryWindowStart" formControlName="deliveryWindowStart"
                                        [showTime]="true" [showIcon]="false" styleClass="w-full">
                                    </p-datePicker>
                                    <label for="deliveryWindowStart">Delivery Window Start</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-clock"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-datePicker inputId="deliveryWindowEnd" formControlName="deliveryWindowEnd"
                                        [showTime]="true" [showIcon]="false" styleClass="w-full">
                                    </p-datePicker>
                                    <label for="deliveryWindowEnd">Delivery Window End</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-2">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-wallet"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="currencyCode" type="text" pInputText formControlName="currencyCode" maxlength="3" />
                                    <label for="currencyCode">Currency</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-2">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-percentage"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-inputNumber
                                        inputId="exchangeRate"
                                        formControlName="exchangeRate"
                                        mode="decimal"
                                        [minFractionDigits]="2"
                                        [maxFractionDigits]="8"
                                        [min]="0.00000001"
                                        styleClass="w-full">
                                    </p-inputNumber>
                                    <label for="exchangeRate">Exchange Rate</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12">
                            <app-flex-field-form
                                [flexFields]="headerFlexFieldControls"
                                [objectName]="'SALES_ORDER'"
                                [owningCompanyId]="currentOwningCompanyId"
                                [existingValues]="headerFlexFieldValues">
                            </app-flex-field-form>
                        </div>
                    </div>
                </app-page-form-section>

                <app-page-form-section
                    icon="pi pi-list-check"
                    title="Order Lines"
                    subtitle="Add product lines, pricing, and optional project overrides.">
                    <div actions>
                        <p-button label="Add Line" icon="pi pi-plus" (click)="addSalesOrderLine()"></p-button>
                    </div>

                    <div formArrayName="salesOrderLines" class="order-main-stack">
                        @for (lineGroup of getSalesOrderLineControls.controls; track i; let i = $index) {
                        <section class="order-section order-surface-card">
                            <div class="order-section__header">
                                <h3>Line {{ i + 1 }}</h3>
                                <div class="order-line-header-actions">
                                    <span class="order-line-total"><span>Line total</span><strong>{{ formatMoney(lineAmounts(lineGroup).total) }}</strong></span>
                                    @if (getSalesOrderLineControls.controls.length > 1) {
                                    <p-button icon="pi pi-trash" styleClass="p-button-danger p-button-text" (click)="removeSalesOrderLine(i)"></p-button>
                                    }
                                </div>
                            </div>

                            <div [formGroupName]="i" class="formgrid grid">
                                <div class="field col-12 md:col-6 xl:col-2">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select
                                                id="product{{i}}"
                                                [options]="products"
                                                formControlName="product"
                                                optionLabel="label"
                                                optionValue="value"
                                                [filter]="true"
                                                appendTo="body"
                                                (onChange)="onProductChange($event, i)"
                                                styleClass="w-full">
                                            </p-select>
                                            <label for="product{{i}}">Product</label>
                                        </p-iftaLabel>
                                        <p-inputGroupAddon>
                                            <p-button icon="pi pi-plus" severity="secondary" type="button" (onClick)="openCreateProductDialog(i)"></p-button>
                                        </p-inputGroupAddon>
                                    </p-inputGroup>
                                    @if (lineGroup.get('product')?.invalid && lineGroup.get('product')?.touched) {
                                    <small class="p-error">Product is required.</small>
                                    }
                                </div>
                                <div class="field col-12 md:col-6 xl:col-2">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-tags"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select
                                                id="variant{{i}}"
                                                [options]="productVariantsFor(lineGroup)"
                                                formControlName="variant"
                                                optionLabel="label"
                                                optionValue="value"
                                                [filter]="true"
                                                appendTo="body"
                                                (onChange)="onProductVariantChange($event, i)"
                                                styleClass="w-full">
                                            </p-select>
                                            <label for="variant{{i}}">Variant</label>
                                        </p-iftaLabel>
                                        <p-inputGroupAddon>
                                            <p-button
                                                icon="pi pi-plus"
                                                severity="secondary"
                                                type="button"
                                                (onClick)="navigateToCreateProductVariant(lineGroup.get('product')?.value)"
                                                [disabled]="!lineGroup.get('product')?.value">
                                            </p-button>
                                        </p-inputGroupAddon>
                                    </p-inputGroup>
                                    @if (lineGroup.get('variant')?.invalid && lineGroup.get('variant')?.touched) {
                                    <small class="p-error">Variant is required.</small>
                                    }
                                </div>
                                <div class="field col-12 md:col-6 xl:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <input id="lineDescription{{i}}" type="text" pInputText formControlName="description" />
                                            <label for="lineDescription{{i}}">Description</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                @if (salesOrderForm.get('crmClient')?.value) {
                                <div class="field col-12 md:col-6 xl:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <input id="crmClientItemCode{{i}}" type="text" pInputText formControlName="crmClientItemCode" />
                                            <label for="crmClientItemCode{{i}}">CRM Client Item No.</label>
                                        </p-iftaLabel>
                                        <p-inputGroupAddon>
                                            <p-button
                                                icon="pi pi-plus"
                                                severity="secondary"
                                                type="button"
                                                pTooltip="Save this item number against the selected CRM client"
                                                [loading]="quickMappingLine === i"
                                                [disabled]="!lineGroup.get('product')?.value || !salesOrderForm.get('crmClient')?.value || !lineGroup.get('crmClientItemCode')?.value"
                                                (onClick)="quickMapClientItemCode(i)">
                                            </p-button>
                                        </p-inputGroupAddon>
                                    </p-inputGroup>
                                    <small>Existing mappings load automatically. Use + to save or update.</small>
                                </div>
                                }
                                <div class="field col-12 md:col-6 xl:col-2 order-line-quantity">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-calculator"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-inputNumber id="quantity{{i}}" formControlName="quantity" mode="decimal" [min]="1" styleClass="w-full"></p-inputNumber>
                                            <label for="quantity{{i}}">Quantity</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                    @if (editMode && lineGroup.get('lineID')?.value) {
                                        <button type="button" class="p-button p-button-text p-button-sm"
                                            (click)="openReleasedQuantityCorrection(lineGroup.get('lineID')?.value)">
                                            Correct quantity with linked releases
                                        </button>
                                    }
                                    @if (lineGroup.get('quantity')?.invalid && lineGroup.get('quantity')?.touched) {
                                    <small class="p-error">
                                        @if (lineGroup.get('quantity')?.hasError('required')) {
                                        Quantity is required.
                                        } @else {
                                        Min value is 1.
                                        }
                                    </small>
                                    }
                                </div>
                                <div class="field col-12 md:col-6 xl:col-2">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-dollar"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-inputNumber
                                                id="unitPrice{{i}}"
                                                formControlName="displayUnitPrice"
                                                mode="decimal"
                                                [minFractionDigits]="4"
                                                [maxFractionDigits]="4"
                                                [min]="0"
                                                (onBlur)="lineGroup.get('unitPrice')?.markAsTouched()"
                                                styleClass="w-full">
                                            </p-inputNumber>
                                            <label for="unitPrice{{i}}">Unit Price</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                    @if (lineGroup.get('unitPrice')?.invalid && lineGroup.get('unitPrice')?.touched) {
                                    <small class="p-error">
                                        @if (lineGroup.get('unitPrice')?.hasError('required')) {
                                        Unit Price is required.
                                        } @else {
                                        Min value is 0.
                                        }
                                    </small>
                                    }
                                    <button type="button" class="p-button p-button-text p-button-sm" [disabled]="lineGroup.disabled"
                                        (click)="openPriceCalculator(lineGroup)">Calculate price from amount</button>
                                </div>
                                @if (priceTargetLine === lineGroup) {
                                <div class="field col-12 order-price-calculator">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-calculator"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-inputNumber inputId="sales-target-net" [(ngModel)]="targetNetAmount" [ngModelOptions]="{standalone: true}"
                                                [min]="0" [minFractionDigits]="currencyDigits" [maxFractionDigits]="currencyDigits"
                                                mode="decimal" styleClass="w-full"></p-inputNumber>
                                            <label for="sales-target-net">Desired before-tax line amount ({{ currencyCode }})</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                    @if (priceTargetPreview(); as preview) {
                                        <span>Unit price {{ preview.price.toFixed(4) }} · Before tax {{ formatMoney(preview.net) }} · Tax {{ formatMoney(preview.tax) }} · Total {{ formatMoney(preview.total) }}</span>
                                    } @else {
                                        <small class="p-error">Enter a positive amount reachable with an eight-decimal unit price and the current quantity.</small>
                                    }
                                    <div class="order-price-calculator__actions">
                                        <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="priceTargetLine = null"></p-button>
                                        <p-button label="Apply price" [disabled]="!priceTargetPreview()" (onClick)="applyTargetPrice()"></p-button>
                                    </div>
                                </div>
                                }
                                <div class="field col-12 md:col-6 xl:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <app-logistics-project-lookup inputId="lineProject{{i}}" formControlName="logisticsProjectId"></app-logistics-project-lookup>
                                            <label for="lineProject{{i}}">Line Project Override</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                @if (lineGroup.get('sourceQuotationLineId')?.value) {
                                <div class="field col-12 md:col-6 xl:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-file-plus"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <input
                                                id="priceSource{{i}}"
                                                type="text"
                                                pInputText
                                                [value]="'CRM quotation line ' + lineGroup.get('sourceQuotationLineId')?.value"
                                                readonly />
                                            <label for="priceSource{{i}}">Source Pricing</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field col-12 md:col-6 xl:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-comment"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <input id="priceOverrideReason{{i}}" type="text" pInputText formControlName="priceOverrideReason" />
                                            <label for="priceOverrideReason{{i}}">Price Override Reason</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                }
                                <div class="field col-12">
                                    <app-flex-field-form
                                        [flexFields]="getLineFlexFieldControls(i)"
                                        [objectName]="'SALES_ORDER_LINE'"
                                        [owningCompanyId]="currentOwningCompanyId"
                                        [existingValues]="lineFlexFieldValues[i]">
                                    </app-flex-field-form>
                                </div>
                            </div>
                        </section>
                        }
                    </div>
                </app-page-form-section>

                <app-page-form-section
                    icon="pi pi-file-edit"
                    title="Internal Notes"
                    subtitle="Capture private commercial notes and coordination context.">
                    <label class="order-editor-label" for="salesOrderNotes">Internal Notes</label>
                    <p-editor
                        id="salesOrderNotes"
                        formControlName="notes"
                        styleClass="order-rich-editor"
                        [style]="{ height: '220px' }">
                    </p-editor>
                </app-page-form-section>
            </div>

            <aside class="order-readiness-aside">
                <section class="readiness-panel">
                    <div class="readiness-panel-header">
                        <div>
                            <span>Review &amp; Create</span>
                            <strong>{{ readinessPercent() }}%</strong>
                        </div>
                        <i class="pi pi-verified"></i>
                    </div>

                    <p-progressBar [value]="readinessPercent()" [showValue]="false"></p-progressBar>

                    @if (aiExtraction; as extraction) {
                    <section class="ai-extraction-card" aria-label="AI extraction review">
                        <div class="ai-extraction-header">
                            <div>
                                <span>AI Extraction</span>
                                <strong>{{ extraction.fileName || 'Uploaded document' }}</strong>
                            </div>
                            <i class="pi pi-sparkles"></i>
                        </div>

                        <div class="ai-extraction-grid">
                            <div>
                                <span>Document</span>
                                <strong>{{ extraction.documentNumber || 'Not detected' }}</strong>
                                <small>{{ extraction.documentType || 'Order document' }}{{ extraction.documentStatus ? ' · ' + extraction.documentStatus : '' }}</small>
                            </div>
                            <div>
                                <span>Customer</span>
                                <strong>{{ extraction.counterpartyName || 'Not detected' }}</strong>
                                <small [class.matched]="aiExtractionCounterpartyMatched" [class.needs-review]="!aiExtractionCounterpartyMatched">
                                    {{ aiExtractionCounterpartyMatched ? 'Matched to CRM' : 'Needs selection' }}
                                </small>
                            </div>
                            <div>
                                <span>Contact</span>
                                <strong>{{ extraction.contactName || extraction.contactEmail || extraction.contactPhone || 'Not detected' }}</strong>
                                @if (extraction.contactName && (extraction.contactEmail || extraction.contactPhone)) {
                                <small>{{ extraction.contactEmail || extraction.contactPhone }}</small>
                                }
                            </div>
                            <div>
                                <span>Dates</span>
                                <strong>{{ extraction.orderDate || 'Not detected' }}</strong>
                                <small>Expected: {{ extraction.expectedDate || 'Not detected' }}</small>
                            </div>
                            <div>
                                <span>Payment Terms</span>
                                <strong>{{ extraction.paymentTerms || 'Not detected' }}</strong>
                            </div>
                            <div>
                                <span>Currency</span>
                                <strong>{{ extraction.currencyCode || 'Not detected' }}</strong>
                            </div>
                            <div>
                                <span>Line Items</span>
                                <strong>{{ extraction.lines?.length || 0 }} extracted</strong>
                                <small [class.needs-review]="aiExtractionUnmatchedProducts > 0">
                                    {{ aiExtractionUnmatchedProducts ? aiExtractionUnmatchedProducts + ' need product selection' : 'All products matched' }}
                                </small>
                            </div>
                            <div>
                                <span>Document Totals</span>
                                <strong>{{ extraction.currencyCode || '' }} {{ extraction.totalAmount ?? 'Not detected' }}</strong>
                                <small>Net {{ extraction.subtotalAmount ?? '-' }} · Tax {{ extraction.taxAmount ?? '-' }}</small>
                            </div>
                            <div>
                                <span>Source File</span>
                                <strong>Will attach on save</strong>
                                <small>Original uploaded document</small>
                            </div>
                        </div>

                        @if (extraction.shippingAddress || extraction.billingAddress) {
                        <div class="ai-extraction-notes">
                            <span>Extracted Addresses</span>
                            <p>{{ extraction.shippingAddress || extraction.billingAddress }}</p>
                        </div>
                        }

                        @if (extraction.notes) {
                        <div class="ai-extraction-notes">
                            <span>AI Notes</span>
                            <p>{{ extraction.notes }}</p>
                        </div>
                        }

                        @if (extraction.warnings?.length) {
                        <div class="ai-extraction-warnings">
                            <span>Review Required</span>
                            @for (warning of extraction.warnings; track $index) {
                            <p><i class="pi pi-exclamation-triangle"></i>{{ warning }}</p>
                            }
                        </div>
                        }
                    </section>
                    }

                    <div class="readiness-status-list">
                        @for (item of readinessItems(); track item.label) {
                        <div class="readiness-status-row" [class.complete]="item.done">
                            <i class="pi" [ngClass]="item.done ? 'pi-check-circle' : 'pi-circle'"></i>
                            <div>
                                <strong>{{ item.label }}</strong>
                                <span>{{ item.hint }}</span>
                            </div>
                        </div>
                        }
                    </div>

                    <div class="totals-card">
                        <span>Document Total</span>
                        <strong>{{ formatMoney(total) }}</strong>
                        <small>Subtotal {{ formatMoney(subtotal) }} | Tax {{ formatMoney(tax) }}</small>
                    </div>

                    <div class="aside-actions">
                        <p-button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            [outlined]="true"
                            (click)="cancel()">
                        </p-button>
                        <p-button
                            [label]="editMode ? 'Save Changes' : 'Create Sales Order'"
                            icon="pi pi-check"
                            type="submit"
                            [disabled]="salesOrderForm.invalid || isLoading || isLocked"
                            [loading]="isLoading">
                        </p-button>
                    </div>
                </section>
            </aside>
        </div>
    </form>
</app-page-shell>
